'use client';

import { ChartConfig } from '@/types/chart-registry';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Info, LinkIcon, Share2, Check } from 'lucide-react';
import Link from 'next/link';
import { Button } from './ui/button';
import moment, { Moment } from 'moment';
import { useState, useEffect, useMemo, createRef, Ref } from 'react';
import { TrimContext } from '@/contexts/trimContext';
import { buildShareUrl, copyToClipboard } from '@/lib/url-encoding';
import { getTraceEvents } from 'next/dist/trace';

import { track } from '@vercel/analytics'
import { ChartSelector } from './chart-selector';
import { useChartSelection } from '@/hooks/useChartSelection';

interface ChartGridProps {
  charts: ChartConfig[];
  onShare?: (url: string) => void;
  initialTrim?: string | null;
}

export function ChartGrid({ charts, onShare, initialTrim }: ChartGridProps) {
  const { selectedCharts, toggleChart, selectAll, deselectAll, applyPreset } = useChartSelection();
  const [trim, setTrim] = useState<Date | null>(null);
  const [selectedTrimBtn, setSelectedTrimBtn] = useState<string>('all')
  const [copied, setCopied] = useState(false);
  const visibleCharts = charts.filter(chart => selectedCharts.has(chart.id));

  const chartRefs: Record<number, Ref<HTMLDivElement>> = useMemo(() => {
    return selectedCharts.keys().reduce((acc, c) => ({...acc, [c]: createRef()}), {})
  }, [selectedCharts])

  const onJumpTo = (c: number) => {
    const ref = chartRefs[c]
    if (!ref) return;
    if (ref instanceof Function) return;
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }

  function setTrimByStr(trimId: string) {
    switch(trimId) {
      case '1y':
        setTrim(moment().subtract(1, 'year').startOf('month').toDate())
        break
      case '6m':
        setTrim(moment().subtract(6, 'months').startOf('month').toDate())
        break
      case '3m':
        setTrim(moment().subtract(3, 'months').startOf('month').toDate())
        break
      case '1m':
        setTrim(moment().subtract(1, 'month').startOf('month').toDate())
        break
      case '15d':
        setTrim(moment().subtract(15, 'days').startOf('day').toDate())
        break
      case 'ytd':
        setTrim(moment().startOf('year').toDate())
        break
      case 'mtd':
        setTrim(moment().startOf('month').toDate())
        break
      default:
        setTrim(null)
      }
  }

  // Sync trim state with initialTrim prop when it changes
  useEffect(() => {
    if (initialTrim !== undefined && initialTrim !== null) {
      setTrimByStr(initialTrim);
      setSelectedTrimBtn(initialTrim||'all')
    }
  }, [initialTrim]);

  const handleShare = async () => {
    const chartIds = Array.from(selectedCharts);
    const timeframe = trim ? getTimeframeFromTrim(trim) : null;
    const url = buildShareUrl(chartIds, timeframe);

    track('catalogue-share', {charts: JSON.stringify(chartIds), timeframe})
    
    await copyToClipboard(url);
    setCopied(true);
    onShare?.(url);
    
    setTimeout(() => setCopied(false), 2000);
  };

  const getTimeframeFromTrim = (trimDate: Date): string | null => {
    const now = moment();
    const trimMoment = moment(trimDate);
    const daysDiff = now.diff(trimMoment, 'days');
    
    if (daysDiff >= 360) return '1y';
    if (daysDiff >= 180) return '6m';
    if (daysDiff >= 90) return '3m';
    if (daysDiff >= 30) return '1m';
    if (daysDiff >= 15) return '15d';
    return null;
  };

  function doSelectTrim(newTrim: string) {
    setTrimByStr(newTrim)
    setSelectedTrimBtn(newTrim)
  }

  return (
    <>
    {/* <Link href="https://forms.gle/kG4pgzxmZ7VfzHiz6" className="fixed bottom-8 right-8 z-10">
      <Button variant='default' className="text-lg animate-wiggle">Send us your feedback 👀</Button>
    </Link> */}
    <div className="grid gap-12 grid-cols-1">
      <div className="flex flex-row gap-1 sm:gap-4 flex-shrink sticky top-0 z-40 bg-background py-2 border-b px-4 lg:px-40">
        <div className="flex grow">
          <ChartSelector
            charts={charts}
            selectedCharts={selectedCharts}
            onSelectAll={selectAll}
            onDeselectAll={deselectAll}
            onToggleChart={toggleChart}
            presets={[]}
            onApplyPreset={applyPreset}
            onJumpTo={onJumpTo}
          />
          
        </div>
        <div className="flex flex-row justify-end">
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='1y'?'default':'outline'} onClick={() => doSelectTrim('1y')}>1Y</Button>
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='6m'?'default':'outline'} onClick={() => doSelectTrim('6m')}>6M</Button>
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='3m'?'default':'outline'} onClick={() => doSelectTrim('3m')}>3M</Button>
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='1m'?'default':'outline'} onClick={() => doSelectTrim('1m')}>1M</Button>
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='15d'?'default':'outline'} onClick={() => doSelectTrim('15d')}>15D</Button>
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='ytd'?'default':'outline'} onClick={() => doSelectTrim('ytd')}>YTD</Button>
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='mtd'?'default':'outline'} onClick={() => doSelectTrim('mtd')}>MTD</Button>
          <Button className="p-1 sm:p-4" variant={selectedTrimBtn==='all'?'default':'outline'} onClick={() => doSelectTrim('all')}>All</Button>
          <Button
            onClick={handleShare}
            variant={copied ? 'default' : 'outline'}
            className="p-1 sm:p-4"
          >
            {copied ? (
              <>
                <Check className="sm:mr-2 sm:h-4 sm:w-4" />
                <span className="hidden sm:flex">Copied!</span>
              </>
            ) : (
              <>
                <Share2 className="sm:mr-2 sm:h-4 sm:w-4" />
                <span className="hidden sm:flex">Share</span>
              </>
            )}
          </Button>
        </div>
      </div>
      {(visibleCharts.length > 0) ?
        <TrimContext.Provider value={trim}>
          {visibleCharts.map((chart) => {
            const ChartComponent = chart.component;
            return (
              <div
                key={chart.id}
                className="flex flex-col gap-2 w-full animate-in fade-in slide-in-from-bottom-4 duration-300 px-4 lg:px-10"
                ref={chartRefs[chart.id]}
              >
                <ChartComponent/>
                { <div className="flex flex-row place-self-end flex-shrink">
                  <Alert className="">
                    <LinkIcon size={20}/>
                    <AlertDescription>
                      {(chart.queryId && !chart.virtual)?
                        <Link
                          target='_blank'
                          href={`https://dune.com/queries/${chart.queryId}`}
                          className="underline"
                        >
                          Query {chart.queryId} on Dune Analytics
                        </Link>
                        :<Link href="#">Derived from Dune Analytics data</Link>}
                    </AlertDescription>
                  </Alert>
                </div> }
              </div>
            );
          })}
        </TrimContext.Provider>
        :
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold mb-2">No Charts Selected</h3>
            <p className="text-muted-foreground">
              Select charts from the top bar menu to display them here
            </p>
          </div>
        </div>
      }
    </div>
    </>
  );
}
